import { CacheableMethod, LoginService } from "@edtell-common";
import { ProgramEditorFormInterface } from "./../interfaces/programs/program-editor-form.interface";
import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { ProgramInterface } from "../interfaces/programs/program.interface";
import { apiURL } from "../../../../environments/environment";
import { ProgramOrDefinitionDisplayInterface } from "../interfaces/programs/program-or-definition-display.interface";
import { ProviderInterface } from "../../providers/interfaces/provider.interface";
import { CatalogCourseCourseTypeDataInterface } from "src/app/modules/catalog/interfaces/catalog-course-course-type-data.interface";
import { ProgramDefinitionFormObjects } from "../interfaces/programs/program-definition-form-objects.interface";
import { ProgramDefinitionForm } from "../interfaces/programs/program-definition-form.interface";
import { ProgramDefinitionProviderDataInterface } from "../interfaces/programs/program-definition-provider-data.interface";
import { ProgramProviderChildrenResponseInterface } from "../interfaces/programs/program-provider-children-response.interface";
import { ProgramDefinitionInterface } from "../interfaces/programs/program-definition.interface";
import { ProgramDefaultDetail } from "../interfaces/programs/program-default-detail.interface";
import { ProgramCourseDataResponseInterface } from "../interfaces/programs/program-course-data-response.interface";
import { ProgramFooterInterface } from "../interfaces/programs/program-editor-footer.interface";
import { ProgramHeaderInterface } from "../interfaces/programs/program-editor-header.interface";
import { LiveProgramViewData } from "../../enrollment/interfaces/live-program-view-data.interface";
import { ProgramHeaderForm } from "../interfaces/programs/program-header-form-interface";
import { ProgramFooterForm } from "../interfaces/programs/program-footer-form-interface";
import { ProgramFooterResponse } from "../interfaces/programs/program-footer-response.interface";
import { ProgramHeaderResponse } from "../interfaces/programs/program-header-response.interface";
import { ProgramCreateFromTemplateRequest } from "../interfaces/programs/program-create-from-template-request.interface";
import { CatalogCourseOverview } from "../../catalog/interfaces/catalog-course-overview.interface";
import { ProgramLineItemPricingDetails } from "../interfaces/programs/program-line-item-pricing-details.interface";
import { ProgramAutocompleteInterface } from "../interfaces/programs/program-autocomplete.interface";
import {
  ProgramLineitemDetailInterface,
  ProgramLineItemDetailResponse,
} from "../interfaces/programs/program-lineitem-detail.interface";
import { ProgramLineItemDetailGroup } from "../interfaces/programs/program-line-item-detail-group.interface";
import { ProgramLineItemInterface } from "../interfaces/programs/program-lineitem.interface";
import { SchoolLandingPage } from "../interfaces/programs/school-landing.interface";
import {
  ProgramSectionsInterface,
  ProgramSectionUpsertRequest,
} from "../interfaces/programs/program-sections.interface";
import { ProgramPricingChanges } from "../../enrollment/interfaces/pricing-changes.interface";
import { TinymceTemplate } from "../../edtell-common/edtell-forms/edtell-forms.interface";

@Injectable({
  providedIn: "root",
})
export class ProgramService {
  private httpOptions: object;
  private textHttpOptions: object;

  private lineItemDetailMap = new Map<
    number,
    ProgramLineitemDetailInterface[]
  >();

  constructor(
    private http: HttpClient,
    private loginService: LoginService,
  ) {
    this.httpOptions = loginService.httpOptions;
    this.textHttpOptions = loginService.textHttpOptions;
  }

  getTinymceTemplates(): TinymceTemplate[] {
    return [
      { title: "Choose a Template", description: "", content: "" },
      {
        title: "#contact-name#",
        description: "Replace Tag with Contact Name from Settings.",
        content: "#contact-name#",
      },
      {
        title: "#contact-email#",
        description: "Replace Tag with Contact Email from Settings.",
        content: "#contact-email#",
      },
      {
        title: "#contact-phone#",
        description: "Replace Tag with Contact Phone from Settings.",
        content: "#contact-phone#",
      },
      {
        title: "#reg-start-date#",
        description: "Replace Tag with Registration End Date from Settings.",
        content: "#reg-start-date#",
      },
      {
        title: "#reg-end-date#",
        description: "Replace Tag with Registration End Date from Settings.",
        content: "#reg-end-date#",
      },
    ];
  }

  @CacheableMethod()
  getProgramById(id) {
    return this.http.get<ProgramInterface>(
      `${apiURL}/customer-app/customer/program?id=${id}`,
      this.httpOptions,
    );
  }

  getAllProgramsByCustomerId(customerId) {
    return this.http.get<ProgramInterface[]>(
      `${apiURL}/customer-app/customer/program/all?customerId=${customerId}`,
      this.httpOptions,
    );
  }

  getAllCoursesFromProgramsByCustomerId(customerId) {
    return this.http.get<{ rank: number; courseName: string }[]>(
      `${apiURL}/customer-app/customer/program/all-courses?customerId=${customerId}`,
      this.httpOptions,
    );
  }

  getProgramFormObjects(customerId) {
    return this.http.get<ProgramDefinitionFormObjects>(
      `${apiURL}/customer-app/customer/program/form-objects?customerId=${customerId}`,
      this.httpOptions,
    );
  }

  getDefinitionEditFormData(definitionId) {
    return this.http.get<ProgramDefinitionForm>(
      `${apiURL}/customer-app/customer/program/definition/edit-form-data?definitionId=${definitionId}`,
      this.httpOptions,
    );
  }

  getAllProgramsAndDefinitionsByCustomerId(customerId) {
    return this.http.get<ProgramOrDefinitionDisplayInterface[]>(
      `${apiURL}/customer-app/customer/program/all-programs-and-definitions?customerId=${customerId}`,
      this.httpOptions,
    );
  }

  updateProgram(programForm: ProgramEditorFormInterface) {
    return this.http.put<ProgramInterface>(
      `${apiURL}/customer-app/customer/program`,
      programForm,
      this.httpOptions,
    );
  }

  upsertProgramSection(req: ProgramSectionUpsertRequest) {
    return this.http.put<ProgramSectionsInterface>(
      `${apiURL}/customer-app/customer/program/section`,
      req,
      this.httpOptions,
    );
  }

  deleteProgramSection(programSection: ProgramSectionsInterface) {
    return this.http.put(
      `${apiURL}/customer-app/customer/program/section/delete`,
      programSection,
      this.httpOptions,
    );
  }

  updateProgramOwner(program: ProgramInterface) {
    return this.http.put<ProgramInterface>(
      `${apiURL}/customer-app/customer/program/owner`,
      program,
      this.httpOptions,
    );
  }

  updateDefaultPrograms(programs: ProgramInterface[]) {
    return this.http.put<boolean>(
      `${apiURL}/customer-app/customer/program/default-program`,
      programs,
      this.httpOptions,
    );
  }

  removeDefaultProgram(defaultProgramId) {
    return this.http.put<ProgramInterface>(
      `${apiURL}/customer-app/customer/program/default-program/remove?defaultProgramId=${defaultProgramId}`,
      null,
      this.httpOptions,
    );
  }

  getDefaultPrograms(customerCode: string) {
    return this.http.get<ProgramInterface[]>(
      `${apiURL}/customer-app/customer/program/default-program?customerCode=${customerCode}`,
      this.httpOptions,
    );
  }

  getSchoolLandingPage(customerCode: string) {
    return this.http.get<SchoolLandingPage>(
      `${apiURL}/customer-app/customer/program/school-landing?customerCode=${customerCode}`,
      this.httpOptions,
    );
  }

  getAllProgramAndDefinitionNamesByCustomerId(customerId) {
    return this.http.get<string[]>(
      `${apiURL}/customer-app/customer/program/all-program-and-definition-names?customerId=${customerId}`,
      this.httpOptions,
    );
  }

  getActiveProviders() {
    return this.http.get<ProviderInterface[]>(
      `${apiURL}/providers-app/provider/program/providers`,
      this.httpOptions,
    );
  }

  getCourseTypeData(providerId) {
    return this.http.get<CatalogCourseCourseTypeDataInterface>(
      `${apiURL}/providers-app/provider/program/provider/course-type-data?providerId=${providerId}`,
      this.httpOptions,
    );
  }

  getPrimaryFooterByProgramId(programId) {
    return this.http.get<ProgramFooterInterface>(
      `${apiURL}/customer-app/customer/program/primary-footer?programId=${programId}`,
      this.httpOptions,
    );
  }

  getPrimaryHeaderByProgramId(programId) {
    return this.http.get<ProgramHeaderInterface>(
      `${apiURL}/customer-app/customer/program/primary-header?programId=${programId}`,
      this.httpOptions,
    );
  }

  getAllHeadersByProgramId(programId) {
    return this.http.get<ProgramHeaderInterface[]>(
      `${apiURL}/customer-app/customer/program/all-headers?programId=${programId}`,
      this.httpOptions,
    );
  }

  getAllHeadersAndPrimaryByProgramId(programId) {
    return this.http.get<ProgramHeaderResponse>(
      `${apiURL}/customer-app/customer/program/all-headers-and-primary?programId=${programId}`,
      this.httpOptions,
    );
  }

  getAllFootersByProgramId(programId) {
    return this.http.get<ProgramFooterInterface[]>(
      `${apiURL}/customer-app/customer/program/all-footers?programId=${programId}`,
      this.httpOptions,
    );
  }

  getAllFootersAndPrimaryByProgramId(programId) {
    return this.http.get<ProgramFooterResponse>(
      `${apiURL}/customer-app/customer/program/all-footers-and-primary?programId=${programId}`,
      this.httpOptions,
    );
  }

  updateHeaders(programHeaderForm: ProgramHeaderForm) {
    return this.http.put<ProgramHeaderForm>(
      `${apiURL}/customer-app/customer/program/headers`,
      programHeaderForm,
      this.httpOptions,
    );
  }

  getMarketPricingChanges(programHeaderId: number) {
    return this.http.get<ProgramPricingChanges>(
      `${apiURL}/customer-app/customer/program/pricing-changes?programHeaderId=${programHeaderId}`,
      this.httpOptions,
    );
  }

  updateFooters(programFooterForm: ProgramFooterForm) {
    return this.http.put<ProgramFooterForm>(
      `${apiURL}/customer-app/customer/program/footers`,
      programFooterForm,
      this.httpOptions,
    );
  }

  createProgramFromTemplate(
    programCreateFromTemplateReq: ProgramCreateFromTemplateRequest,
  ) {
    return this.http.post<ProgramInterface>(
      `${apiURL}/customer-app/customer/program/create-from-template`,
      programCreateFromTemplateReq,
      this.httpOptions,
    );
  }

  copyProgramToCustomer(programId, customerId, copyMarkup, programName?) {
    let url = `${apiURL}/customer-app/customer/program/copy?programId=${programId}&customerId=${customerId}&copyMarkup=${copyMarkup}`;
    if (programName) {
      url += `&programName=${programName}`;
    }
    return this.http.post<boolean>(url, null, this.httpOptions);
  }

  /*
    Program Definition Endpoints
   */
  createProgramDefinition(createRequest: ProgramDefinitionForm) {
    return this.http.post(
      `${apiURL}/customer-app/customer/program/definition`,
      createRequest,
      this.httpOptions,
    );
  }

  updateProgramDefinition(createRequest: ProgramDefinitionForm) {
    return this.http.put(
      `${apiURL}/customer-app/customer/program/definition`,
      createRequest,
      this.httpOptions,
    );
  }

  updateProgramDefinitionOwner(definition: ProgramDefinitionInterface) {
    return this.http.put<ProgramDefinitionInterface>(
      `${apiURL}/customer-app/customer/program/definition/owner`,
      definition,
      this.httpOptions,
    );
  }

  deleteProgramDefinition(id: number) {
    return this.http.delete(
      `${apiURL}/customer-app/customer/program/definition?id=${id}`,
      this.httpOptions,
    );
  }

  copyProgramDefinitionToCustomer(definitionId: number, customerId: number) {
    return this.http.post(
      `${apiURL}/customer-app/customer/program/definition/copy?definitionId=${definitionId}&customerId=${customerId}`,
      null,
      this.httpOptions,
    );
  }

  // createProgram(program) {
  //   return this.http.post<ProgramInterface>(`${apiURL}/customer-app/customer/program`, program, this.httpOptions);
  // }

  createProgram(program) {
    return this.http.post<ProgramInterface>(
      `${apiURL}/providers-app/provider/program`,
      program,
      this.httpOptions,
    );
  }

  createDefinition(definition) {
    return this.http.post(
      `${apiURL}/customer-app/customer/program/definition`,
      definition,
      this.httpOptions,
    );
  }

  isValidCopy(customerId, programId, definitionId) {
    if (definitionId === null) {
      return this.http.get<boolean>(
        `${apiURL}/customer-app/customer/program/is-valid-copy?customerId=${customerId}&programId=${programId}`,
        this.httpOptions,
      );
    }
    if (programId === null) {
      return this.http.get<boolean>(
        `${apiURL}/customer-app/customer/program/is-valid-copy?customerId=${customerId}&definitionId=${definitionId}`,
        this.httpOptions,
      );
    }
  }

  getProviderChildren(providerId) {
    return this.http.get<ProgramProviderChildrenResponseInterface[]>(
      `${apiURL}/providers-app/provider/program/children?providerId=${providerId}`,
      this.httpOptions,
    );
  }

  getDefinitionProviderData(definitionId) {
    return this.http.get<ProgramDefinitionProviderDataInterface[]>(
      `${apiURL}/customer-app/customer/program/provider/data?definitionId=${definitionId}`,
      this.httpOptions,
    );
  }

  getCanDeleteDefinition(id) {
    return this.http.get<boolean>(
      `${apiURL}/customer-app/customer/program/definition/can-delete?id=${id}`,
      this.httpOptions,
    );
  }

  getProgramEditorFormDataByProgramId(programId) {
    return this.http.get<ProgramEditorFormInterface>(
      `${apiURL}/customer-app/customer/program/edit-form-data?programId=${programId}`,
      this.httpOptions,
    );
  }

  /**
   *
   * @param lineItemId
   * @param markupId Used for the cacheable method annotation. The results can change dependent on the markup. Not used within the method.
   * @returns
   */
  @CacheableMethod()
  getProgramLineItemDetails(lineItemId: number | number[]) {
    return this.http.get<ProgramLineItemDetailResponse[]>(
      `${apiURL}/customer-app/customer/program/line-item/details?lineItemId=${lineItemId}`,
      this.httpOptions,
    );
  }

  getProgramLineItemDetailGroups(programId: number) {
    return this.http.get<ProgramLineItemDetailGroup[]>(
      `${apiURL}/customer-app/customer/program/line-item/details/groups?programId=${programId}`,
      this.httpOptions,
    );
  }

  /**
   *
   * @param courseAvailabilityId
   * @param programId
   * @param markupId Used for the cacheable method annotation. The results can change dependent on the markup. Not used within the method.
   * @returns
   */
  @CacheableMethod()
  getProgramLineItemPricingDetails(
    courseAvailabilityId: number,
    programId: number
  ) {
    return this.http.get<ProgramLineItemPricingDetails>(
      `${apiURL}/customer-app/customer/program/line-item/pricing-details?courseAvailabilityId=${courseAvailabilityId}&programId=${programId}`,
      this.httpOptions,
    );
  }

  /* Program API */

  deleteProgram(programId) {
    return this.http.delete(
      `${apiURL}/customer-app/customer/program?programId=${programId}`,
      this.httpOptions,
    );
  }

  @CacheableMethod()
  getLineItemFromCourseId(courseId, programId, customerId) {
    return this.http.get<ProgramCourseDataResponseInterface>(
      `${apiURL}/customer-app/customer/program/line-item/from-course?courseId=${courseId}&programId=${programId}&customerId=${customerId}`,
      this.httpOptions,
    );
  }

  /* Program Default Detail API */

  getProgramDefaultDetailByCustomerId(customerId: number) {
    return this.http.get<ProgramDefaultDetail>(
      `${apiURL}/customer-app/customer/program/default/detail?customerId=${customerId}`,
      this.httpOptions,
    );
  }

  /* Live Program View */

  @CacheableMethod()
  getLiveProgramViewData(homeInstruction: boolean, programId?: number, schoolCode?: string) {
    let queryStr =
      programId != null ? `programId=${programId}` : `schoolCode=${schoolCode}`;
    if (programId != null && schoolCode != null) {
      queryStr = `programId=${programId}&schoolCode=${schoolCode}&homeInstruction=${homeInstruction}`;
    }

    return this.http.get<LiveProgramViewData>(
      `${apiURL}/customer-app/customer/program/live-view?${queryStr}`,
      this.httpOptions,
    );
  }

  getIsProgramBlockedFromGoogle(programId) {
    return this.http.get<boolean>(
      `${apiURL}/customer-app/customer/program/is-blocked-from-google?programId=${programId}`,
      this.httpOptions,
    );
  }

  getProgramAutocompleteResults(searchString) {
    return this.http.get<ProgramAutocompleteInterface[]>(
      `${apiURL}/customer-app/customer/program/autocomplete?searchString=${searchString}`,
      this.httpOptions,
    );
  }

  getProgramLineItem(lineItemId: number, programId?: number) {
    let url = `${apiURL}/customer-app/customer/program/line-item?lineItemId=${lineItemId}`;
    url += programId ? `&programId=${programId}` : "";
    return this.http.get<ProgramLineItemInterface>(url, this.httpOptions);
  }

  /* Course Overview */
  @CacheableMethod()
  getCatalogCourseOverview(
    courseId: number,
    programId: number,
    schoolCode: string,
    homeInstruction = false
  ) {
    return this.http.get<CatalogCourseOverview>(
      `${apiURL}/providers-app/provider/program/course/overview?courseId=${courseId}&programId=${programId}&schoolCode=${schoolCode}&homeInstruction=${homeInstruction}`,
      this.httpOptions,
    );
  }
}
